import {
  Dropdown as NextUIDropdown,
  DropdownItem as NextUIDropdownItem,
  DropdownItemProps as NextUIDropdownItemProps,
  DropdownMenu as NextUIDropdownMenu,
  DropdownTrigger as NextUIDropdownTrigger,
} from "@heroui/react";
import clsx from "clsx";
import { TRACK_EVENTS } from "core/consts";
import { TourElement } from "core/types";
import { Button } from "ds/ui";
import { NewChip } from "dsl/atoms/SettingsNavigation";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LucideProps,
} from "lucide-react";
import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useTracking } from "react-tracking";

export type DropdownNavbarMenuItem = Pick<
  NextUIDropdownItemProps,
  "description" | "onPress" | "href"
> & {
  hrefMatch?: string;
  icon?: React.ReactElement<LucideProps>;
  id: string;
  label: string;
  showNewChip?: boolean;
  tour?: TourElement;
};

export type DropdownNavbarProps = {
  id: string;
  items: DropdownNavbarMenuItem[];
  trackingEventName: keyof typeof TRACK_EVENTS;
};

function useActiveDropdownItem({ items }: { items: DropdownNavbarMenuItem[] }) {
  const location = useLocation();

  const activeItem = useMemo(() => {
    const matches = items
      .map((item) => ({
        item,
        match: item.hrefMatch
          ? matchPath(item.hrefMatch, location.pathname)
          : false,
      }))
      .filter(({ match }) => match !== null && match !== false);

    // Rank the matches by the length of the hrefMatch string
    matches.sort((a, b) => {
      const aLength = a.item.hrefMatch?.length || 0;
      const bLength = b.item.hrefMatch?.length || 0;
      return aLength - bLength;
    });

    // Return the item with the longest matching pattern (most specific)
    return matches.length > 0 ? matches[matches.length - 1].item : null;
  }, [items, location.pathname]);

  return activeItem;
}

export function DropdownNavbar({
  id,
  items,
  trackingEventName,
}: DropdownNavbarProps) {
  const { trackEvent } = useTracking();
  const activeItem = useActiveDropdownItem({ items });

  return (
    <NextUIDropdown
      classNames={{
        base: "before:shadow-md w-full",
        trigger: "w-full desktop:w-[256px] justify-between",
      }}
      shouldCloseOnInteractOutside={(_e) => true}
    >
      <NextUIDropdownTrigger>
        <Button
          className={clsx(
            "uppercase",
            "focus:ring-0 aria-expanded:scale-100 data-[pressed=true]:scale-[1]",
          )}
          color="primary"
          disableRipple
          endContent={
            <>
              <ChevronUpIcon
                aria-hidden={true}
                className="hidden group-aria-expanded:block"
                size={16}
              />
              <ChevronDownIcon
                aria-hidden={true}
                className="group-aria-expanded:hidden"
                size={16}
              />
            </>
          }
          id={id}
          size="sm"
          variant="faded"
        >
          <span>{activeItem?.label ?? ""}</span>
        </Button>
      </NextUIDropdownTrigger>
      <NextUIDropdownMenu
        classNames={{
          base: "w-full max-h-[45vh] tablet:max-h-[302px] overflow-y-auto outline-none",
          list: "gap-1 w-[87.5vw] tablet:w-[92.5vw] desktop:w-full",
        }}
        color="primary"
        selectionMode="none"
      >
        {items.map(({ description, id, label, onPress, showNewChip }) => {
          const isCurrentPage = id === activeItem?.id;

          return (
            <NextUIDropdownItem
              key={id}
              aria-current={isCurrentPage ? "page" : undefined}
              className={clsx(
                "group !outline-none-v3 rounded-lg !bg-white p-2 ring-1",
                "data-[focus=true]:!bg-primary-light data-[focus=true]:!outline data-[focus=true]:!outline-1 data-[focus=true]:!outline-offset-1 data-[focus=true]:ring-primary",
                "data-[hover=true]:!bg-primary-light data-[hover=true]:text-gray-900 data-[hover=true]:!outline data-[hover=true]:!outline-1 data-[hover=true]:!outline-offset-1 data-[hover=true]:ring-primary",
                {
                  "!bg-primary-light ring-primary": isCurrentPage,
                  "ring-transparent": !isCurrentPage,
                },
              )}
              endContent={isCurrentPage ? <CheckIcon size={16} /> : undefined}
              id={id}
              textValue={label}
              onPress={(e) => {
                trackEvent({
                  item: id,
                  name: TRACK_EVENTS[trackingEventName],
                });
                requestAnimationFrame(() => onPress?.(e));
              }}
            >
              <div className="flex justify-between">
                <h2 className="text-sm font-medium uppercase text-gray-900">
                  {label}
                </h2>
                {showNewChip && <NewChip />}
              </div>
              {description && (
                <p className="text-xs font-normal text-gray-600">
                  {description}
                </p>
              )}
            </NextUIDropdownItem>
          );
        })}
      </NextUIDropdownMenu>
    </NextUIDropdown>
  );
}
